import { SUPPORT_ACCOUNT_EMAIL } from '@hotjar/webapp/constants/support';
import { getOrganizations, organizationSelector } from '@hotjar/organization';
import type { Site } from '@hotjar/site';
import { help as trackHelp } from '../tracking/help';
import { goToKnowledgeBase } from './actions';

/**
 * Check if support has access to the organization of the user's current site.
 * TODO: test if we can simplify this to just check `currentSite.organization.users` now that we
 * have a real model.
 * @param currentUserId The user's id (maybe we don't need this)
 * @param currentSite The user's current site
 * @private
 */
const isSupportAccessGranted = async (
  currentUserId: number,
  currentSite: Site,
): Promise<boolean> => {
  const organizations = await getOrganizations(currentUserId);
  const organization = await organizationSelector(currentSite.organization.id)(organizations);

  return organization.users.some((user) => user.email === SUPPORT_ACCOUNT_EMAIL);
};

/**
 * Open the knowledgebase,
 * checking first if support access has been granted to the organization of the user's current
 * site.
 * If support access is not granted, but the user has access to do so, show them the option to
 * do so (currently provided by `noAccessCallback`).
 *
 * N.B. if the intention of this action is to open the knowledgebase regardless of the success
 * or otherwise of the call to check access or the dismissal of or erroring of a modal, then this
 * should be refactored to catch those errors and always complete with {@see goToKnowledgeBase}.
 * @param hasGrantSupportPermission
 * @param currentUserId The user's id
 * @param currentSite The user's current site
 * @param noAccessCallback Callback to prompt the user to allow support access. Receives a
 * `done` function parameter which should always be called when complete.
 */
// eslint-disable-next-line func-params-args/func-params
export const launchHelp = async (
  hasGrantSupportPermission: boolean,
  currentUserId: number,
  currentSite: Site,
  noAccessCallback?: (done: () => void) => void,
  currentLang = 'en',
): Promise<void> => {
  trackHelp('Get help', currentSite);

  if (!hasGrantSupportPermission) {
    // If this is not an org admin, they cannot grant access to Hotjar support.
    goToKnowledgeBase(currentSite, currentLang);

    return;
  }

  let supportHasAccess: boolean;
  try {
    supportHasAccess = await isSupportAccessGranted(currentUserId, currentSite);
  } catch {
    // N.B. if the intention is to go to help regardless of access, we should set
    // `supportHasAccess` to `false` here and continue instead of returning.
    return;
  }

  if (supportHasAccess) {
    // If support has access, continue to KB.
    goToKnowledgeBase(currentSite, currentLang);
  } else if (noAccessCallback) {
    // Else ask for access and then continue to KB.
    noAccessCallback(() => goToKnowledgeBase(currentSite, currentLang));
  }
};
